import { Button, Flex, Input, Spin, Table } from 'antd'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import { getColumns, createCrossPageSelectionHandlers } from './TestCaseColums'

import { AppliedFilter } from './filter/AppliedFilter'

import {
    setFilter,
    setPagination,
    setSearchValue,
    testCaseList,
    testRunState,
} from '../../../../redux/testRun/testRunList.slice'

import { apiGetTestCaseList } from '../../../../redux/testRun/testRun.service'

import AddResultDrawer from './form/AddResultDrawer'

import FilterPopover from './filter/FilterPopover'

import ViewTestCaseModal from './form/ViewTestCaseModal'

import { resolveTestRunProjectId } from '../../../shared/TestRunTabs'

import { isValidArray } from '../../../../utils/helpers'

import styles from '../../../../testrun.module.css'

const useDebounce = (value, delay = 400) => {
    const [v, setV] = useState(value)

    useEffect(() => {
        const t = setTimeout(() => setV(value), delay)

        return () => clearTimeout(t)
    }, [value, delay])

    return v
}

const TestCaseList = ({ reportId = '', projectId: projectIdProp }) => {
    const dispatch = useDispatch()

    const projectId = resolveTestRunProjectId(projectIdProp)

    const {
        testCase = [],

        testCaseListLoading = false,

        pagination: { pageSize = 10, currentPage = 0 },

        filter,

        defaultFilter,

        searchValue,
    } = useSelector(testRunState)

    const testCaseListData = testCase?.data || []

    const filterCount =
        (filter?.epic?.length ? 1 : 0) +
        (filter.sprint ? 1 : 0) +
        (filter?.tags?.length ? 1 : 0) +
        (filter?.status ? 1 : 0) +
        (searchValue?.length ? 1 : 0)

    const [stateTestRun, setStateTestRun] = useState({
        drawerEdit: false,

        filterPopover: false,

        selectedTCID: [],

        viewTCID: null,
    })

    const [selectedRowKeys, setSelectedRowKeys] = useState([])

    const [selectingAll, setSelectingAll] = useState(false)

    const [search, setSearch] = useState('')

    const [sortInfo, setSortInfo] = useState({
        sortField: 'createdAt',
        sortType: 'desc',
    })

    const handleAction = (act, id) => {
        if (act === 'view') {
            setStateTestRun((prev) => ({
                ...prev,

                viewTCID: id,
            }))

            return
        }

        setStateTestRun({
            ...stateTestRun,

            drawerEdit: true,

            editType: act,

            selectedTCID: [id],
        })
    }

    const debouncedSearch = useDebounce(search, 500)

    const isFirstSearchSync = useRef(true)

    useEffect(() => {
        const trimmed = debouncedSearch?.trim() || ''

        dispatch(setSearchValue(trimmed))

        if (isFirstSearchSync.current) {
            isFirstSearchSync.current = false

            return
        }

        dispatch(setPagination({ pageSize, currentPage: 1 }))
    }, [debouncedSearch, dispatch, pageSize])

    const clearAllFilter = () => {
        dispatch(setFilter(defaultFilter))

        dispatch(setSearchValue(''))

        setSearch('')
    }

    const clearSpecificFilter = (field) => {
        dispatch(
            setFilter({
                ...filter,

                [field]: ['epic', 'tags'].includes(field) ? [] : '',
            })
        )

        if (field === 'search') {
            setSearch('')

            dispatch(setSearchValue(''))
        }
    }

    const buildListQuery = useCallback(
        (skip, limit) => {
            const tagsArray = filter?.tags?.map((item) => item?.value) || []

            const epic = isValidArray(filter?.epic)
                ? filter.epic.map((item) => item?.value)
                : []

            const sprint = filter?.sprint?.value || ''

            const filterStatus = filter?.status?.value
                ? filter.status.value
                : filter?.status

            return {
                skip,

                limit,

                filter: {
                    epic: epic || [],

                    sprint: sprint || '',

                    ...(isValidArray(filter?.tags) ? { tags: tagsArray } : {}),

                    status: filterStatus,
                },

                search: { title: searchValue, testId: searchValue },

                sortField: sortInfo.sortField,

                sortType: sortInfo.sortType,

                reportId,
            }
        },

        [searchValue, filter, reportId, sortInfo]
    )

    const listQuery = useMemo(
        () => buildListQuery((currentPage - 1) * pageSize, pageSize),

        [buildListQuery, currentPage, pageSize]
    )

    const listQueryRef = useRef(listQuery)

    listQueryRef.current = listQuery

    useEffect(() => {
        if (!reportId) return

        dispatch(testCaseList(listQuery))
    }, [dispatch, reportId, listQuery])

    const handleTestCaseList = useCallback(() => {
        if (!reportId) return

        dispatch(testCaseList(listQueryRef.current))
    }, [dispatch, reportId])

    const totalCount = testCase?.count || 0

    const columns = useMemo(
        () =>
            getColumns(
                handleAction,
                {
                    selectedCount: selectedRowKeys.length,
                    totalCount,
                },
                sortInfo
            ),
        [handleAction, selectedRowKeys.length, totalCount, sortInfo]
    )

    const fetchAllMatchingIds = useCallback(async () => {
        if (!reportId || !totalCount) return []

        const res = await apiGetTestCaseList(buildListQuery(0, totalCount))

        const list = Array.isArray(res?.data?.data) ? res.data.data : []

        return list.map((row) => row._id).filter(Boolean)
    }, [buildListQuery, reportId, totalCount])

    const { handleRowSelectionChange, handleSelectAll } = useMemo(
        () =>
            createCrossPageSelectionHandlers({
                setSelectedRowKeys,
                setSelectingAll,
                fetchAllMatchingIds,
            }),
        [fetchAllMatchingIds]
    )

    const rowSelection = useMemo(
        () => ({
            selectedRowKeys,
            onChange: handleRowSelectionChange,
            onSelectAll: handleSelectAll,
            preserveSelectedRowKeys: true,
            getCheckboxProps: () => ({
                disabled: selectingAll,
            }),
        }),
        [
            handleRowSelectionChange,
            handleSelectAll,
            selectingAll,
            selectedRowKeys,
        ]
    )

    const pagination = {
        current: currentPage,

        pageSize,

        total: testCase?.count || 0,

        showSizeChanger: true,

        pageSizeOptions: ['10', '20', '50', '100'],

        showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,

        onChange: (page, size) => {
            dispatch(
                setPagination({
                    pageSize: size,

                    currentPage: size !== pageSize ? 1 : page,
                })
            )
        },
    }

    const hasSelected = selectedRowKeys.length > 0

    return (
        <Flex gap="middle" vertical className={styles.reportWrapper}>
            <div className={styles.sectionHeader}>
                <h3 className={styles.pageTitle}>Test Cases</h3>
                <div className={styles.toolbarActions}>
                    <FilterPopover
                        stateTestRun={stateTestRun}
                        setStateTestRun={setStateTestRun}
                        projectId={projectId}
                    >
                        <Button className={styles.secondaryButton}>
                            Filter
                        </Button>
                    </FilterPopover>

                    <Button
                        className={styles.secondaryButton}
                        disabled={!hasSelected}
                        onClick={() =>
                            setStateTestRun({
                                selectedTCID: selectedRowKeys,

                                drawerEdit: true,

                                editType: 'bulk',
                            })
                        }
                    >
                        Bulk Edit
                    </Button>

                    <Input
                        placeholder="Search test cases (title, id)"
                        allowClear
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className={styles.searchInput}
                    />
                </div>
            </div>

            {!!filterCount && (
                <AppliedFilter
                    data={filter}
                    searchValue={searchValue}
                    onClearAll={clearAllFilter}
                    onRemove={clearSpecificFilter}
                />
            )}

            <div className={styles.tableSection}>
                {selectingAll && (
                    <div className={styles.selectAllLoader}>
                        <Spin size="small" />
                        <span>Selecting all test cases...</span>
                    </div>
                )}
                <Table
                    className={styles.reportTable}
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={testCaseListData}
                    rowKey="_id"
                    loading={testCaseListLoading || selectingAll}
                    pagination={pagination}
                    scroll={{ x: true }}
                    onChange={(_pagination, _filters, sorter, extra) => {
                        if (extra?.action !== 'sort') return

                        const next = Array.isArray(sorter) ? sorter[0] : sorter
                        const field = next?.field || next?.columnKey

                        if (!next?.order || !field) {
                            setSortInfo({
                                sortField: 'createdAt',
                                sortType: 'desc',
                            })
                            dispatch(
                                setPagination({ pageSize, currentPage: 1 })
                            )
                            return
                        }

                        if (field !== 'testId') {
                            return
                        }

                        setSortInfo({
                            sortField: field,
                            sortType:
                                next.order === 'ascend' ? 'asc' : 'desc',
                        })
                        dispatch(setPagination({ pageSize, currentPage: 1 }))
                    }}
                />
            </div>

            <ViewTestCaseModal
                isOpen={Boolean(stateTestRun.viewTCID)}
                onClose={() =>
                    setStateTestRun((prev) => ({ ...prev, viewTCID: null }))
                }
                reportId={reportId}
                testCaseId={stateTestRun.viewTCID}
            />

            <AddResultDrawer
                stateTestRun={stateTestRun}
                setStateTestRun={setStateTestRun}
                selectedRowKeys={selectedRowKeys}
                setSelectedRowKeys={setSelectedRowKeys}
                reportId={reportId}
                handleTestCaseList={handleTestCaseList}
            />
        </Flex>
    )
}

export default TestCaseList
